// VX L4-0 — KdRegionBarView: 지역 셀렉 상시 노출 바의 «보이는 부분»(순수 · props 만 · 훅 없음).
// 상태·직렬화·이동은 KdRegionBar(클라이언트 래퍼)가 기존 region-scope 로 붙인다. 이 파일은 서버에서도 그대로 렌더된다.
//
// 구성(경로형 2행):
//   1행  「지역」 + 경로  전국 › 부산 › 해운대구   (각 마디가 버튼 — 누르면 그 단으로 올라간다)
//   2행  «다음 단» 칩 한 줄 — 전국이면 시·도, 시·도가 정해졌으면 그 시·도의 시·군(「부산 전체」 맨 앞)
//
// ⛔ 선택지는 <button> 이다 — `?r=` URL 을 <a href> 로 내보내지 않는다(크롤러가 파라미터 변형을 줍지 않게 · R14b 규칙 승계).
// ⛔ sticky 로 세워도 되게 배경은 불투명(--card). blur 없음(DS_RULES#1-6).
// 칩 시각 높이 34px, 히트 영역은 ::after 로 44px(DS_RULES#1-5 ② — 레이아웃을 키우지 않는다).
// 시안 채점(2026-10-12, docs/v0/scores/L40_media.md): 경로형(b) 21.5 · 2행 칩(a) 18.5 · 언더라인 탭(c) 18 → b 정착.
import React from 'react';

/** count = 현장 수(regions.site_count) — 2행 칩에만 작게. 없음·0 이면 그리지 않는다. */
export interface KdRegionOption { code: string; label: string; count?: number | null }

export interface KdRegionBarViewProps {
  /** 시·도(호출부가 정렬). */
  tops: KdRegionOption[];
  /** 선택된 시·도의 시·군(없으면 빈 배열). 「{시·도} 전체」 는 컴포넌트가 맨 앞에 붙인다. */
  leaves: KdRegionOption[];
  /** 선택된 시·도 코드(전국이면 null). */
  topCode: string | null;
  /** 선택된 시·군 코드(시·도 전체면 null). */
  leafCode: string | null;
  /** code=null → 전국. 시·도 코드 → 그 시·도 전체. 시·군 코드 → 그 시·군. */
  onPick?: (code: string | null) => void;
  className?: string;
}

export const NATIONAL = '전국';

function Opt({ code, label, count, current, onPick, cls }: { code: string | null; label: string; count?: number | null; current: boolean; onPick?: (code: string | null) => void; cls: string }) {
  const n = typeof count === 'number' && Number.isFinite(count) && count > 0 ? count.toLocaleString('en-US') : null;
  return (
    <li>
      <button type="button" className={cls} data-code={code ?? 'national'} aria-current={current ? 'true' : undefined} onClick={onPick ? () => onPick(code) : undefined}>
        {label}
        {n ? <span className="kd-rb-n"><span className="e-sr">현장 </span>{n}<span className="e-sr">곳</span></span> : null}
      </button>
    </li>
  );
}

export default function KdRegionBarView({ tops, leaves, topCode, leafCode, onPick, className }: KdRegionBarViewProps) {
  if (!tops || tops.length === 0) return null;
  const top = topCode ? tops.find((t) => t.code === topCode) ?? null : null;
  const leaf = top && leafCode ? leaves.find((l) => l.code === leafCode) ?? null : null;
  const showLeaves = !!top && leaves.length > 0;

  return (
    <nav className={className ? `kd-rb ${className}` : 'kd-rb'} aria-label="지역 선택" data-kd="region-bar" data-kd-region={leaf?.code ?? top?.code ?? 'national'}>
      <div className="kd-rb-r">
        <span className="kd-rb-k" data-rd="label">지역</span>
        <ol className="kd-rb-path" aria-label="현재 지역">
          <Opt code={null} label={NATIONAL} current={!top} onPick={onPick} cls="kd-rb-p" />
          {top ? <Opt code={top.code} label={top.label} current={!leaf} onPick={onPick} cls="kd-rb-p" /> : null}
          {leaf ? <Opt code={leaf.code} label={leaf.label} current onPick={onPick} cls="kd-rb-p" /> : null}
        </ol>
      </div>
      <div className="kd-rb-r kd-rb-r2">
        {showLeaves && top ? (
          <ul className="kd-rb-l" aria-label={`${top.label} 시·군·구`}>
            <Opt code={top.code} label={`${top.label} 전체`} count={top.count} current={!leaf} onPick={onPick} cls="kd-rb-c" />
            {leaves.map((l) => (
              <Opt key={l.code} code={l.code} label={l.label} count={l.count} current={leaf?.code === l.code} onPick={onPick} cls="kd-rb-c" />
            ))}
          </ul>
        ) : (
          <ul className="kd-rb-l" aria-label="시·도">
            {tops.map((t) => (
              <Opt key={t.code} code={t.code} label={t.label} count={t.count} current={top?.code === t.code} onPick={onPick} cls="kd-rb-c" />
            ))}
          </ul>
        )}
      </div>
    </nav>
  );
}
